/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import {
  forwardRef,
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
  type ComponentPropsWithoutRef,
} from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import ReactECharts from 'echarts-for-react';
import { Menu, MenuItem } from '@mui/material';
import { colors } from '../../utils/colors';
import { getPrice } from '../../utils/currency';
import moment from '../../utils/moment';
import { capitalizeAllWords } from '../../utils/text-helpers';
import type { DashboardDataQuery } from '../../apollo';
import { resetButtonStyles, getDownMedia } from '../../styles';
import { WrapperWithOverlay } from '../../styled/portfolio';
import { BurgerIcon } from '../icons';
import { Text } from '../texts';
import { EmptyIndexPortfolioPopup as DefEmptyIndexPortfolioPopup } from './EmptyIndexPortfolioPopup.tsx';

export type GraphProps = {
  data: DashboardDataQuery['getPortfolioGraphData'];
  emptyPortfolio: boolean;
} & ComponentPropsWithoutRef<'section'>;

type RangeValue = '1M' | '3M' | '6M' | 'YTD' | '1Y' | 'ALL' | 'CUSTOM';

const IndexPortfolioGraph = forwardRef<HTMLElement, GraphProps>(
  ({ data, emptyPortfolio, ...props }, ref) => {
    const { t } = useTranslation();
    const chartRef = useRef<ReactECharts>(null);

    const portfolioGraphData = useMemo(
      () =>
        data.map(({ date, value }) => [
          moment.utc(date).valueOf(),
          +value.toFixed(2),
        ]),
      [data]
    );

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [activeRange, setActiveRange] = useState<RangeValue>('YTD');

    const getRangeZoom = useCallback(
      (range: RangeValue) => {
        if (!portfolioGraphData.length) {
          return { start: 0, end: 100 };
        }

        const firstPoint = portfolioGraphData[0][0];
        const lastPoint = portfolioGraphData[portfolioGraphData.length - 1][0];
        const totalDuration = lastPoint - firstPoint;

        if (totalDuration <= 0) {
          return { start: 0, end: 100 };
        }

        let startPoint: number;

        switch (range) {
          case '1M':
            startPoint = moment(lastPoint).subtract(1, 'months').valueOf();
            break;
          case '3M':
            startPoint = moment(lastPoint).subtract(3, 'months').valueOf();
            break;
          case '6M':
            startPoint = moment(lastPoint).subtract(6, 'months').valueOf();
            break;
          case 'YTD':
            startPoint = moment(lastPoint).startOf('year').valueOf();
            break;
          case '1Y':
            startPoint = moment(lastPoint).subtract(1, 'years').valueOf();
            break;
          case 'ALL':
          default:
            startPoint = firstPoint;
            break;
        }

        const startPercent = Math.max(
          0,
          ((startPoint - firstPoint) / totalDuration) * 100
        );

        return {
          start: startPercent,
          end: 100,
        };
      },
      [portfolioGraphData]
    );

    const [zoomValues, setZoomValues] = useState<{
      start: number;
      end: number;
    }>(() => getRangeZoom(activeRange));

    const isSinglePoint = portfolioGraphData.length === 1;

    const option = useMemo(
      () => ({
        tooltip: {
          trigger: 'axis',
          formatter: (params: $TSFixMe) => {
            const [point] = params;
            const dateStr = capitalizeAllWords(
              moment(point.value[0]).format('dddd, MMM D, YYYY')
            );
            const valueStr = getPrice(point.value[1]);
            return `${dateStr}<br/><span style="color:${point.color}">●</span> ${point.seriesName}: <b>${valueStr}</b>`;
          },
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          textStyle: {
            color: colors.black,
          },
        },
        grid: {
          left: '1%',
          right: '1%',
          top: '40px',
          bottom: 100,
          containLabel: true,
        },
        legend: {
          show: true,
          bottom: 0,
          left: 'center',
          icon: 'rect',
          itemWidth: 9,
          itemHeight: 9,
          textStyle: {
            fontSize: 12,
            color: colors.darkBlue,
          },
          data: [t('DASHBOARD_PAGE__indexPortfolioTitle')],
        },
        xAxis: {
          type: 'time',
          // Add paddings for single point to be centered
          boundaryGap: isSinglePoint ? ['20%', '20%'] : false,
          axisLine: { lineStyle: { color: colors.lightGray } },
        },
        yAxis: {
          type: 'value',
          scale: true,
          // Add vertical gap to prevent the line from touching the grid edges
          boundaryGap: ['10%', '10%'],
          axisLabel: {
            formatter: (value: number) =>
              getPrice(value, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
          },
          splitLine: {
            lineStyle: {
              type: 'dashed',
            },
          },
        },
        // Hide dataZoom if only a single point exists to remove the top artifact line
        dataZoom: isSinglePoint
          ? []
          : [
              {
                type: 'inside',
                start: zoomValues.start,
                end: zoomValues.end,
              },
              {
                type: 'slider',
                start: zoomValues.start,
                end: zoomValues.end,
                bottom: 45,
                height: 28,
                showDetail: false,
                showDataShadow: true,
              },
            ],
        series: [
          {
            name: t('DASHBOARD_PAGE__indexPortfolioTitle'),
            type: 'line',
            symbol: isSinglePoint ? 'circle' : 'none',
            symbolSize: 8,
            showSymbol: isSinglePoint,
            sampling: isSinglePoint ? undefined : 'lttb',
            itemStyle: {
              color: colors.blue,
            },
            // Disable area filling for a single point to avoid rendering issues
            areaStyle: isSinglePoint
              ? { opacity: 0 }
              : {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: 'rgba(58, 77, 233, 0.3)' },
                    { offset: 1, color: 'rgba(58, 77, 233, 0)' },
                  ]),
                },
            data: portfolioGraphData,
          },
        ],
      }),
      [t, portfolioGraphData, zoomValues, isSinglePoint]
    );

    const handleRangeChange = useCallback((range: RangeValue) => {
      setActiveRange(range);
    }, []);

    const handleCloseMenu = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleDownload = useCallback(
      (type: 'png' | 'jpeg' | 'svg') => {
        handleCloseMenu();
        const chartInstance = chartRef.current?.getEchartsInstance();
        if (!chartInstance) return;

        const dataURL = chartInstance.getDataURL({
          type,
          pixelRatio: 2,
          backgroundColor: '#fff',
        });

        const link = document.createElement('a');
        link.href = dataURL;
        link.download = `portfolio-graph.${type === 'jpeg' ? 'jpg' : type}`;
        link.click();
      },
      [handleCloseMenu]
    );

    useEffect(() => {
      if (activeRange !== 'CUSTOM') {
        setZoomValues(getRangeZoom(activeRange));
      }
    }, [activeRange, getRangeZoom]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    return (
      <Wrapper ref={ref} $overlayed={emptyPortfolio} {...props}>
        <Header>
          <RangeSelector>
            <RangeLabel>{t('HIGHCHARTS__rangeSelectorZoom')}</RangeLabel>
            {ranges.map((range) => (
              <RangeButton
                key={range.value}
                $active={activeRange === range.value}
                onClick={() => handleRangeChange(range.value)}
              >
                {range.label}
              </RangeButton>
            ))}
          </RangeSelector>
          <BurgerButton
            aria-controls="graph-menu"
            aria-haspopup="true"
            onClick={handleClick}
          >
            <BurgerIcon fill={colors.darkBlue} />
          </BurgerButton>
          <Menu
            id="graph-menu"
            anchorEl={anchorEl}
            keepMounted
            hideBackdrop
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            MenuListProps={{ onMouseLeave: handleCloseMenu }}
            onClose={handleCloseMenu}
            onClick={handleCloseMenu}
          >
            <LinkMenuItem onClick={() => handleDownload('png')}>
              {t('DASHBOARD_PAGE__indexPortfolioDownloadPNG')}
            </LinkMenuItem>
            <LinkMenuItem onClick={() => handleDownload('jpeg')}>
              {t('DASHBOARD_PAGE__indexPortfolioDownloadJPEG')}
            </LinkMenuItem>
            <LinkMenuItem onClick={() => handleDownload('svg')}>
              {t('DASHBOARD_PAGE__indexPortfolioDownloadSVG')}
            </LinkMenuItem>
          </Menu>
        </Header>
        <ReactECharts
          ref={chartRef}
          option={option}
          style={{ width: '100%', height: 377 }}
          opts={{ renderer: 'svg' }}
          notMerge={true}
          lazyUpdate={true}
          onEvents={{
            datazoom: (params: {
              batch?: { start: number; end: number }[];
              start?: number;
              end?: number;
            }) => {
              const [batchItem] = params.batch ?? [];

              if (batchItem) {
                const { start, end } = batchItem;
                setZoomValues({ start, end });
                setActiveRange('CUSTOM');
              } else if (
                params.start !== undefined &&
                params.end !== undefined
              ) {
                setZoomValues({ start: params.start, end: params.end });
                setActiveRange('CUSTOM');
              }
            },
          }}
        />

        {emptyPortfolio && <EmptyIndexPortfolioPopup />}
      </Wrapper>
    );
  }
);

const Wrapper = styled(WrapperWithOverlay).attrs({
  as: 'section',
})`
  display: flex;
  flex-direction: column;
  min-height: 190px;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    padding: 0 10px;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
`;

const RangeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${getDownMedia('sm')} {
    gap: 4px;
  }
`;

const RangeLabel = styled(Text)`
  margin-right: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.darkBlue};
`;

const RangeButton = styled.button<{ $active?: boolean }>`
  ${resetButtonStyles};
  width: 40px;
  height: 24px;
  cursor: pointer;
  font-size: 8px;
  font-weight: 600;
  color: ${colors.darkBlue};
  transition: all 0.2s;

  ${({ $active }) =>
    $active &&
    css`
      background-color: ${colors.hawkesBlue};
      color: ${colors.black};
    `}

  &:hover {
    background-color: ${({ $active }) => ($active ? '#eef1fe' : '#f5f5f5')};
  }

  ${getDownMedia('sm')} {
    width: 30px;
    height: 20px;
  }
`;

const BurgerButton = styled.button`
  ${resetButtonStyles};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: ${colors.darkBlue};

  svg {
    color: ${colors.darkBlue};
  }

  &:hover {
    background-color: ${colors.hawkesBlue};
  }
`;

const LinkMenuItem = styled(MenuItem)`
  font-size: 12px;
`;

const EmptyIndexPortfolioPopup = styled(DefEmptyIndexPortfolioPopup)`
  padding-left: 22px;
  padding-right: 22px;
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 3;
  transform: translate(-50%, -50%);
`;

const ranges = [
  { label: '1M', value: '1M' },
  { label: '3M', value: '3M' },
  { label: '6M', value: '6M' },
  { label: 'YTD', value: 'YTD' },
  { label: '1Y', value: '1Y' },
  { label: 'ALL', value: 'ALL' },
] as const satisfies readonly { label: string; value: RangeValue }[];

export { IndexPortfolioGraph };
